import React from 'react';
import './SalesforceDataExport.css';

const SalesforceDataExport = () => {
    return (
        <div className="help-page">
            <div className="help-container">
                <header className="help-header">
                    <h1>How to Export Files from Salesforce</h1>
                    <p className="help-subtitle">Follow this guide to export ContentVersion, ContentDocumentLink, and file attachments from Salesforce for migration.</p>
                </header>

                <nav className="help-toc">
                    <h3>Table of Contents</h3>
                    <ol>
                        <li><a href="#overview">Overview</a></li>
                        <li><a href="#prerequisites">Prerequisites</a></li>
                        <li><a href="#data-export">Using Salesforce Data Export</a></li>
                        <li><a href="#data-loader">Using Data Loader (Alternative)</a></li>
                        <li><a href="#files-needed">Files You Need</a></li>
                        <li><a href="#troubleshooting">Troubleshooting</a></li>
                    </ol>
                </nav>

                <section id="overview" className="help-section">
                    <h2>1. Overview</h2>
                    <p>
                        To migrate your Salesforce files and attachments to HubSpot, you need to export three types of data:
                    </p>
                    <ul className="file-list">
                        <li>
                            <strong>ContentVersion.csv</strong> - Contains metadata about all files (Title, Extension, Size, ContentDocumentId, etc.)
                        </li>
                        <li>
                            <strong>ContentDocumentLink.csv</strong> - Contains relationships between files and Salesforce records (which file belongs to which Account, Contact, Opportunity, etc.)
                        </li>
                        <li>
                            <strong>ZIP Files</strong> - The actual binary files (documents, images, PDFs, etc.)
                        </li>
                    </ul>
                </section>

                <section id="prerequisites" className="help-section">
                    <h2>2. Prerequisites</h2>
                    <ul>
                        <li>Salesforce Administrator access or appropriate permissions</li>
                        <li>Access to Setup menu in Salesforce</li>
                        <li>Sufficient storage space on your computer for the export files</li>
                    </ul>
                </section>

                <section id="data-export" className="help-section">
                    <h2>3. Using Salesforce Data Export (Recommended)</h2>
                    <p>Salesforce's built-in Data Export feature is the easiest way to get all required files.</p>

                    <div className="step-card">
                        <h3>Step 1: Navigate to Data Export</h3>
                        <ol>
                            <li>Log in to Salesforce as an Administrator</li>
                            <li>Click the <strong>Gear icon</strong> in the top right corner</li>
                            <li>Select <strong>Setup</strong></li>
                            <li>In the Quick Find box, type <strong>"Data Export"</strong></li>
                            <li>Click on <strong>Data Export</strong> under the Data section</li>
                        </ol>
                    </div>

                    <div className="step-card">
                        <h3>Step 2: Schedule or Start Export</h3>
                        <ol>
                            <li>Click <strong>"Export Now"</strong> or <strong>"Schedule Export"</strong></li>
                            <li>Select the encoding (UTF-8 recommended)</li>
                            <li>Check <strong>"Include images, documents, and attachments"</strong></li>
                            <li>Check <strong>"Include Salesforce Files and Salesforce CRM Content document versions"</strong></li>
                        </ol>
                        <div className="info-box">
                            <strong>Important:</strong> Make sure both checkboxes for files are selected to include all attachments and documents.
                        </div>
                    </div>

                    <div className="step-card">
                        <h3>Step 3: Select Objects to Export</h3>
                        <p>Ensure these objects are selected:</p>
                        <ul>
                            <li><strong>ContentVersion</strong> - Required</li>
                            <li><strong>ContentDocumentLink</strong> - Required</li>
                            <li><strong>ContentDocument</strong> - Optional but recommended</li>
                            <li><strong>Attachment</strong> - If you have legacy attachments</li>
                        </ul>
                    </div>

                    <div className="step-card">
                        <h3>Step 4: Download Export Files</h3>
                        <ol>
                            <li>Salesforce will send you an email when the export is ready</li>
                            <li>Return to <strong>Setup → Data Export</strong></li>
                            <li>Download all the ZIP files provided</li>
                            <li>Extract the files to find your CSV files</li>
                        </ol>
                        <div className="warning-box">
                            <strong>Note:</strong> Export links are only available for 48 hours. Download them promptly.
                        </div>
                    </div>
                </section>

                <section id="data-loader" className="help-section">
                    <h2>4. Using Data Loader (Alternative)</h2>
                    <p>If you need more control over the export, you can use Salesforce Data Loader.</p>

                    <div className="step-card">
                        <h3>Export ContentVersion.csv</h3>
                        <ol>
                            <li>Open Data Loader and click <strong>Export</strong></li>
                            <li>Log in to your Salesforce org</li>
                            <li>Select <strong>ContentVersion</strong> object</li>
                            <li>Choose a destination file (ContentVersion.csv)</li>
                            <li>Select all fields or at minimum:
                                <ul>
                                    <li>Id</li>
                                    <li>ContentDocumentId</li>
                                    <li>Title</li>
                                    <li>FileExtension</li>
                                    <li>PathOnClient</li>
                                    <li>VersionData</li>
                                    <li>ContentSize</li>
                                    <li>CreatedDate</li>
                                </ul>
                            </li>
                            <li>Click <strong>Finish</strong> to export</li>
                        </ol>
                    </div>

                    <div className="step-card">
                        <h3>Export ContentDocumentLink.csv</h3>
                        <ol>
                            <li>In Data Loader, click <strong>Export</strong></li>
                            <li>Select <strong>ContentDocumentLink</strong> object</li>
                            <li>Choose a destination file (ContentDocumentLink.csv)</li>
                            <li>Select all fields or at minimum:
                                <ul>
                                    <li>Id</li>
                                    <li>ContentDocumentId</li>
                                    <li>LinkedEntityId</li>
                                    <li>ShareType</li>
                                    <li>Visibility</li>
                                </ul>
                            </li>
                            <li>Click <strong>Finish</strong> to export</li>
                        </ol>
                    </div>

                    <div className="step-card">
                        <h3>Export Binary Files</h3>
                        <p>To export the actual files, you have two options:</p>
                        <ol>
                            <li><strong>Use the Salesforce Data Export</strong> (recommended) - Follow the steps in Section 3</li>
                            <li><strong>Use a third-party tool</strong> - Tools like Salesforce Inspector or custom scripts can download files via the API</li>
                        </ol>
                    </div>
                </section>

                <section id="files-needed" className="help-section">
                    <h2>5. Files You Need to Upload</h2>
                    <p>After export, you should have:</p>

                    <div className="file-checklist">
                        <div className="checklist-item">
                            <span className="checklist-icon">1</span>
                            <div>
                                <strong>ContentVersion.csv</strong>
                                <p>Usually named <code>ContentVersion.csv</code> in your export</p>
                            </div>
                        </div>
                        <div className="checklist-item">
                            <span className="checklist-icon">2</span>
                            <div>
                                <strong>ContentDocumentLink.csv</strong>
                                <p>Usually named <code>ContentDocumentLink.csv</code> in your export</p>
                            </div>
                        </div>
                        <div className="checklist-item">
                            <span className="checklist-icon">3</span>
                            <div>
                                <strong>ZIP Files containing binary files</strong>
                                <p>One or more ZIP files named like <code>WE_...</code> containing the actual documents</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="troubleshooting" className="help-section">
                    <h2>6. Troubleshooting</h2>

                    <div className="faq-item">
                        <h4>Q: I don't see ContentVersion in my export options</h4>
                        <p>A: Make sure you have the "Salesforce Files" feature enabled. ContentVersion is part of Salesforce Files (Lightning Experience). If you're using Classic, you might only have Attachments.</p>
                    </div>

                    <div className="faq-item">
                        <h4>Q: My export is taking too long</h4>
                        <p>A: Large organizations with many files can take several hours or even days to export. Salesforce will email you when it's ready. You can also try scheduling the export during off-peak hours.</p>
                    </div>

                    <div className="faq-item">
                        <h4>Q: The ZIP files are very large</h4>
                        <p>A: This is normal. Salesforce splits exports into multiple ZIP files, each up to 512MB. Upload them one at a time to our system.</p>
                    </div>

                    <div className="faq-item">
                        <h4>Q: I'm getting "Access Denied" errors</h4>
                        <p>A: You need Administrator permissions or the "Manage Data Exports" permission to perform data exports. Contact your Salesforce admin.</p>
                    </div>

                    <div className="faq-item">
                        <h4>Q: Some files are missing from my export</h4>
                        <p>A: Check if those files have restricted sharing settings. Also ensure you selected all the required checkboxes during export setup.</p>
                    </div>
                </section>

                <footer className="help-footer">
                    <p>Need more help? Contact our support team or refer to the <a href="https://help.salesforce.com/s/articleView?id=sf.data_export.htm" target="_blank" rel="noopener noreferrer">official Salesforce documentation</a>.</p>
                </footer>
            </div>
        </div>
    );
};

export default SalesforceDataExport;
